import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faTimes, faSearch } from "@fortawesome/free-solid-svg-icons";

import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomButton from "../../Components/CustomButton";
import CustomInput from "../../Components/CustomInput";
import CustomModal from "../../Components/CustomModal";
import Loader from '../../Components/Loader';

import "./style.css";

import { useCreateFamilyLinkMutation, useGetUsersForLinkingQuery } from "../../Redux/Apis/Family";
import { placeholderImage } from "../../Assets/images";

const UserSelector = ({
  label,
  selectedUser,
  setSelectedUser,
  searchValue,
  setSearchValue,
  users,
  isLoading,
  excludeId
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const filteredUsers = users?.data?.filter(u => u._id !== excludeId) || [];

    return (
      <div className="selectUser">
        <label>{label}</label>
        {selectedUser ? (
          <div className="selectedUser">
            <img 
              src={selectedUser.picture ? `${process.env.REACT_APP_IMAGE_ENDPOINT}${selectedUser.picture}` : placeholderImage} 
              alt={selectedUser.name} 
            />
            <div className="userInfo">
              <div className="userName">{selectedUser.name}</div>
              <div className="userEmail">{selectedUser.email}</div>
            </div>
            <button type="button" className="clearBtn" onClick={() => setSelectedUser(null)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ) : (
          <div className="userSearchContainer">
            <div className="searchInputWrapper">
              <FontAwesomeIcon icon={faSearch} className="searchIcon" />
              <input
                type="text"
                className="userSearchInput"
                placeholder={`Search for ${label.toLowerCase()}...`}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
              />
            </div>
            {showDropdown && searchValue && (
              <div className="userDropdown">
                {isLoading ? (
                  <div className="dropdownLoading">Searching...</div>
                ) : filteredUsers.length > 0 ? (
                  filteredUsers.map(user => (
                    <div 
                      key={user._id} 
                      className="dropdownItem"
                      onClick={() => {
                        setSelectedUser(user);
                        setSearchValue('');
                        setShowDropdown(false);
                      }}
                    >
                      <img 
                        src={user.picture ? `${process.env.REACT_APP_IMAGE_ENDPOINT}${user.picture}` : placeholderImage} 
                        alt={user.name} 
                      />
                      <div>
                        <div className="userName">{user.name}</div>
                        <div className="userEmail">{user.email}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="dropdownEmpty">No users found</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
};

export const CreateFamilyLink = () => {
  const navigate = useNavigate();
  const [createFamilyLink, { isLoading: isCreating }] = useCreateFamilyLinkMutation();

  const [parentId, setParentId] = useState(null);
  const [childId, setChildId] = useState(null);
  const [parentSearch, setParentSearch] = useState('');
  const [childSearch, setChildSearch] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState('');

  // Fetch users for parent selection
  const { data: parentUsers, isLoading: loadingParents } = useGetUsersForLinkingQuery({
    search: parentSearch,
    excludeId: childId,
  }, { skip: !parentSearch && !parentId });

  // Fetch users for child selection
  const { data: childUsers, isLoading: loadingChildren } = useGetUsersForLinkingQuery({
    search: childSearch,
    excludeId: parentId,
  }, { skip: !childSearch && !childId });

  useEffect(() => {
    document.title = 'Landing List | Create Marshall Connection';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!parentId || !childId) {
      setError('Please select both a guardian and a member');
      return;
    }

    if (parentId === childId) {
      setError('Guardian and member cannot be the same user');
      return;
    }

    try {
      await createFamilyLink({ parentId, childId }).unwrap();
      setShowSuccessModal(true);
    } catch (err) {
      setError(err?.data?.message || 'Failed to create connection');
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/family-management');
  };

  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);

  useEffect(() => {
    setParentId(selectedParent?._id || null);
  }, [selectedParent]);

  useEffect(() => {
    setChildId(selectedChild?._id || null);
  }, [selectedChild]);

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <button className="backBtn" onClick={() => navigate(-1)}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </button>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <div className="dashCard">
                <h2 className="mainTitle mb-4">Create Marshall Connection</h2>
                <p className="text-muted mb-4">
                  Create a connection between guardian and member accounts. Once connected, Marshall protection will be enabled.
                </p>

                <form onSubmit={handleSubmit} className="createLinkForm">
                  <UserSelector
                    label="Guardian"
                    selectedUser={selectedParent}
                    setSelectedUser={setSelectedParent}
                    searchValue={parentSearch}
                    setSearchValue={setParentSearch}
                    users={parentUsers}
                    isLoading={loadingParents}
                    excludeId={childId}
                  />

                  <UserSelector
                    label="Member"
                    selectedUser={selectedChild}
                    setSelectedUser={setSelectedChild}
                    searchValue={childSearch}
                    setSearchValue={setChildSearch}
                    users={childUsers}
                    isLoading={loadingChildren}
                    excludeId={parentId}
                  />

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  <div className="formActions">
                    <CustomButton 
                      type="button" 
                      text="Cancel" 
                      className="secondaryButton" 
                      onClick={() => navigate(-1)} 
                    />
                    <CustomButton 
                      type="submit" 
                      text={isCreating ? "Creating..." : "Create Connection"} 
                      className="primaryButton" 
                      disabled={isCreating || !parentId || !childId}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <CustomModal 
          show={showSuccessModal} 
          close={handleSuccessClose} 
          success 
          heading='Marshall connection created successfully!' 
        />
      </DashboardLayout>

      <style jsx>{`
        .backBtn {
          background: none;
          border: none;
          color: #28a745;
          font-size: 14px;
          cursor: pointer;
          padding: 8px 0;
        }
        .backBtn:hover {
          text-decoration: underline;
        }
        .userSearchContainer {
          position: relative;
        }
        .searchInputWrapper {
          position: relative;
        }
        .searchIcon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }
        .userSearchInput {
          width: 100%;
          padding: 12px 12px 12px 40px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }
        .userSearchInput:focus {
          outline: none;
          border-color: #28a745;
        }
        .userDropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          z-index: 100;
          max-height: 250px;
          overflow-y: auto;
        }
        .dropdownItem {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
        }
        .dropdownItem:hover {
          background-color: #f8f9fa;
        }
        .dropdownItem:last-child {
          border-bottom: none;
        }
        .dropdownItem img {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
        }
        .dropdownLoading, .dropdownEmpty {
          padding: 16px;
          text-align: center;
          color: #666;
        }
        .formActions {
          display: flex;
          gap: 12px;
          margin-top: 32px;
        }
        .alert {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        .alert-danger {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
      `}</style>
    </>
  );
};

export default CreateFamilyLink;

